// Composite the low-resolution cloud buffer over the beauty pass at full
// resolution. The clouds are raymarched at a fraction of screen resolution
// (cloud_raymarch_frag.glsl); a 9-tap Catmull-Rom bicubic upscale keeps the
// upsampled deck smooth without the blockiness of plain bilinear.

varying vec2 vUv;

uniform sampler2D tDiffuse;   // beauty pass (full resolution)
uniform sampler2D uClouds;    // premultiplied clouds (low resolution)
uniform vec2      uCloudRes;  // low-res target size, in pixels

// Catmull-Rom bicubic — 9 bilinear taps. `texSize` is the source size in pixels.
vec4 sampleCatmullRom(sampler2D tex, vec2 uv, vec2 texSize) {
  vec2 samplePos = uv * texSize;
  vec2 texPos1 = floor(samplePos - 0.5) + 0.5;
  vec2 f = samplePos - texPos1;

  vec2 w0 = f * (-0.5 + f * (1.0 - 0.5 * f));
  vec2 w1 = 1.0 + f * f * (-2.5 + 1.5 * f);
  vec2 w2 = f * (0.5 + f * (2.0 - 1.5 * f));
  vec2 w3 = f * f * (-0.5 + 0.5 * f);

  vec2 w12 = w1 + w2;
  vec2 offset12 = w2 / w12;

  vec2 texPos0 = (texPos1 - 1.0) / texSize;
  vec2 texPos3 = (texPos1 + 2.0) / texSize;
  vec2 texPos12 = (texPos1 + offset12) / texSize;

  vec4 result = vec4(0.0);
  result += texture2D(tex, vec2(texPos0.x,  texPos0.y))  * w0.x  * w0.y;
  result += texture2D(tex, vec2(texPos12.x, texPos0.y))  * w12.x * w0.y;
  result += texture2D(tex, vec2(texPos3.x,  texPos0.y))  * w3.x  * w0.y;
  result += texture2D(tex, vec2(texPos0.x,  texPos12.y)) * w0.x  * w12.y;
  result += texture2D(tex, vec2(texPos12.x, texPos12.y)) * w12.x * w12.y;
  result += texture2D(tex, vec2(texPos3.x,  texPos12.y)) * w3.x  * w12.y;
  result += texture2D(tex, vec2(texPos0.x,  texPos3.y))  * w0.x  * w3.y;
  result += texture2D(tex, vec2(texPos12.x, texPos3.y))  * w12.x * w3.y;
  result += texture2D(tex, vec2(texPos3.x,  texPos3.y))  * w3.x  * w3.y;
  return result;
}

void main() {
  vec4 scene = texture2D(tDiffuse, vUv);
  vec4 cloud = sampleCatmullRom(uClouds, vUv, uCloudRes);
  // Catmull-Rom can overshoot slightly at edges — clamp so it never darkens the
  // scene or produces negative coverage.
  cloud = max(cloud, vec4(0.0));
  cloud.a = min(cloud.a, 1.0);

  // Clouds are premultiplied: scene * (1 - a) + cloudRGB.
  vec3 color = scene.rgb * (1.0 - cloud.a) + cloud.rgb;
  gl_FragColor = vec4(color, scene.a);
}
